import {differenceInCalendarDays, format} from 'date-fns';
import {utcToZonedTime} from 'date-fns-tz';

const JST_TIME_ZONE = 'Asia/Tokyo';

/**
 * 絶対時刻を、日本時間(Asia/Tokyo)の日時を持つ Date に変換する。
 *
 * 返り値は、ローカル getter(`getMonth()` / `getHours()` / `getDay()` 等)や
 * date-fns の `format` が JST の値を返すよう構築されている。
 * 呼び元は、生の Date に対する既存の整形処理をこの関数で包んだ Date に対しておこなうだけでよい。
 *
 * 注意: 返り値は表示・暦日計算用に JST の日時を持たせた Date であり、絶対時刻としては正しくない。
 * getTime() 等での絶対時刻としての比較・保存・API送信には使わないこと。
 */
export const toJstDate = (date: Date): Date =>
  utcToZonedTime(date, JST_TIME_ZONE);

/**
 * 有効な日付文字列かどうかをチェックする
 *
 * @param dateString - チェックする日付文字列
 * @returns 有効な日付の場合true、それ以外はfalse
 *   - 空文字列または未定義の場合はfalse
 *   - 無効な日付形式の場合はfalse
 *   - 1900年以前の日付の場合はfalse
 *
 * @example
 * // 有効な日付
 * isValidDate('2025-10-21') // => true
 * isValidDate('2025-10-21T12:00:00') // => true
 *
 * // 無効な日付
 * isValidDate('') // => false
 * isValidDate('invalid-date') // => false
 * isValidDate('1899-12-31') // => false
 * isValidDate(undefined) // => false
 */
export const isValidDate = (dateString?: string): boolean => {
  if (!dateString || dateString.trim() === '') return false;
  const date = new Date(dateString);
  return !Number.isNaN(date.getTime()) && date.getFullYear() > 1900;
};

/**
 * 引数に渡した日付が今日から何日前・何日後かを返す
 *
 * @param date - ISO形式などの日付文字列
 * @returns 今日から指定日付までの日数
 *   - 正の数: 未来の日付（例: 5 = 5日後）
 *   - 0: 今日と同じ日付
 *   - 負の数: 過去の日付（例: -3 = 3日前）
 *
 * @example
 * // 5日後の日付
 * getDaysFromToday('2025-10-26') // => 5
 *
 * // 今日と同じ日付
 * getDaysFromToday('2025-10-21') // => 0
 *
 * // 3日前の日付
 * getDaysFromToday('2025-10-18') // => -3
 */
export const getDaysFromToday = (date: string): number => {
  // JSTで差分を取る
  const targetDate = toJstDate(new Date(date));
  const today = toJstDate(new Date());

  // 時刻の影響を排除するため、differenceInCalendarDaysを使用
  return differenceInCalendarDays(targetDate, today);
};

/**
 * 日付文字列から時間を H:mm 形式で取得する
 *
 * @param dateString - ISO形式などの日付文字列
 * @returns H:mm 形式の時間文字列（24時間形式、時のゼロ埋めなし）
 *
 * @example
 * // 午後3時30分
 * getTimeFromDate('2025-10-21T15:30:00') // => '15:30'
 *
 * // 午前9時5分
 * getTimeFromDate('2025-10-21T09:05:00') // => '9:05'
 *
 * // 深夜0時
 * getTimeFromDate('2025-10-21T00:00:00') // => '0:00'
 */
export const getTimeFromDate = (dateString: string): string => {
  // JSTで整形
  return format(toJstDate(new Date(dateString)), 'H:mm');
};

/**
 * JST（日本標準時）の翌日深夜0時をUTCのDateとして返す
 *
 * クッキーの有効期限をJST深夜0時にリセットしたい場合に使用する。
 *
 * @returns JST翌日00:00:00に相当するUTC Date
 *
 * @example
 * // 2026-05-27 20:00 JSTに呼んだ場合
 * getJSTMidnightExpiry() // => 2026-05-27 15:00:00 UTC (= 2026-05-28 00:00:00 JST)
 */
export const getJSTMidnightExpiry = (): Date => {
  const jstOffsetMs = 9 * 60 * 60 * 1000;
  const jstNow = new Date(Date.now() + jstOffsetMs);
  jstNow.setUTCHours(24, 0, 0, 0);
  return new Date(jstNow.getTime() - jstOffsetMs);
};
